/**
 * Server data, cache first (PRD 16: "Screens draw from cache first and refresh quietly").
 *
 * The signed-in person's own figures are kept in localStorage under her person id so the
 * app opens with her last balance and "Last updated 08:42" when the signal is gone. The
 * cache is wiped at sign-out. Nothing that moves money works from the cache: mutations
 * always go to the server.
 */
import { QueryClient } from '@tanstack/react-query'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import type { Persister } from '@tanstack/react-query-persist-client'
import { ApiError } from './client'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      gcTime: 1000 * 60 * 60 * 24,
      refetchOnWindowFocus: true,
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
    },
    mutations: { retry: false },
  },
})

const CACHE_PREFIX = 'tisunge.cache.'

/**
 * What may be kept on the device for offline viewing: her own figures and the group's
 * statements by member number. Officer views (names, phone numbers, approvals, the close)
 * and anything else stay in memory only, so a shared or lost phone never holds them.
 */
const PERSISTED_RESOURCES = new Set(['home', 'projection', 'loans', 'statement', 'statements', 'activity',
  'queries', 'notifications', 'pledge', 'guarantees', 'eligibility', 'channels', 'destinations', 'rules-summary',
  'payments'])

export function isPersistable(queryKey: readonly unknown[]): boolean {
  if (queryKey[0] === 'me') return true
  return queryKey[0] === 'group' && PERSISTED_RESOURCES.has(String(queryKey[2]))
}

let persistence = true

/** Tests turn the device cache off, so one test's late write never feeds the next test's screen. */
export function setPersistence(enabled: boolean): void {
  persistence = enabled
}

export function personPersister(personId: string): Persister | null {
  if (!persistence) return null
  try {
    return createSyncStoragePersister({ storage: window.localStorage, key: CACHE_PREFIX + personId })
  } catch {
    return null
  }
}

/** Sign-out: forget every cached figure on this device. */
export function clearPersonCaches(client: QueryClient = queryClient): void {
  client.clear()
  try {
    for (const key of Object.keys(window.localStorage)) {
      if (key.startsWith(CACHE_PREFIX)) window.localStorage.removeItem(key)
    }
  } catch {
    // storage unavailable: nothing was persisted
  }
}
